// apps/dashboard/src/lib/first-touch.ts
import Cookies from 'js-cookie';
import { clientCookieAttributes } from './cookies';
import { hasAnalyticsConsent } from './consent';
import { getEntryReferrerOrigin } from './entry-referrer';

/**
 * First-touch attribution cookie. Written ONCE on a visitor's first tracked page view
 * (never overwritten), read at signup and sent to POST /v1/auth/sync so the account can be
 * credited to the channel that first brought the visitor in. Gated on the same analytics
 * consent as the event SDK; without consent nothing is stored and nothing is sent.
 *
 * Stores an origin and a query-less path, never a full URL: those can carry search terms
 * or tokens. Cookie attributes/domain match aks_anon so it is shared with the dashboard.
 *
 * Duplicated per B2C app (same convention as anon-id.ts/consent.ts) — keep the copies in sync.
 */
export const FIRST_TOUCH_COOKIE = 'aks_ft';

// Same lifetime as aks_anon (~400 days).
const FIRST_TOUCH_MAX_AGE_DAYS = 400;
const MAX_PATH_LENGTH = 200;
const MAX_UTM_LENGTH = 100;
const MAX_ORIGIN_LENGTH = 200;

export interface FirstTouch {
  v: 1;
  /** Entry pathname, no query string. */
  p: string;
  /** utm_source / utm_medium / utm_campaign. */
  us: string | null;
  um: string | null;
  uc: string | null;
  /** Entry referrer origin; null for direct, same-site or local dev traffic. */
  r: string | null;
  /** ISO timestamp of the first touch. */
  t: string;
}

const INTERNAL_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]']);

// Our own properties (and dev hosts) are navigation, not acquisition channels.
function isInternalHost(hostname: string): boolean {
  return (
    hostname === 'aksademia.com' ||
    hostname.endsWith('.aksademia.com') ||
    INTERNAL_HOSTS.has(hostname) ||
    hostname === window.location.hostname
  );
}

function externalReferrerOrigin(): string | null {
  const origin = getEntryReferrerOrigin();
  if (!origin || origin.length > MAX_ORIGIN_LENGTH) return null;
  try {
    return isInternalHost(new URL(origin).hostname) ? null : origin;
  } catch {
    return null;
  }
}

function utmValue(params: URLSearchParams, key: string): string | null {
  const value = params.get(key)?.trim();
  return value ? value.slice(0, MAX_UTM_LENGTH) : null;
}

/**
 * Sets aks_ft on the first consented page view. No-ops when the cookie already exists
 * (first touch is immutable) or analytics consent is absent.
 */
export function captureFirstTouch(pathname: string, search?: string): void {
  if (typeof document === 'undefined') return;
  if (!hasAnalyticsConsent()) return;
  if (Cookies.get(FIRST_TOUCH_COOKIE)) return;

  const params = new URLSearchParams(search ?? '');
  const touch: FirstTouch = {
    v: 1,
    p: pathname.split(/[?#]/)[0].slice(0, MAX_PATH_LENGTH),
    us: utmValue(params, 'utm_source'),
    um: utmValue(params, 'utm_medium'),
    uc: utmValue(params, 'utm_campaign'),
    r: externalReferrerOrigin(),
    t: new Date().toISOString(),
  };
  // js-cookie URL-encodes the JSON value on write and decodes it on read.
  Cookies.set(FIRST_TOUCH_COOKIE, JSON.stringify(touch), clientCookieAttributes(FIRST_TOUCH_MAX_AGE_DAYS));
}

/**
 * Reads aks_ft for the signup call. Tolerates a missing or malformed cookie (returns null),
 * and re-checks consent so a visitor who has since opted out is not attributed.
 */
export function readFirstTouch(): FirstTouch | null {
  if (typeof document === 'undefined') return null;
  if (!hasAnalyticsConsent()) return null;

  const raw = Cookies.get(FIRST_TOUCH_COOKIE);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const c = parsed as Record<string, unknown>;
    if (c.v !== 1 || typeof c.p !== 'string' || typeof c.t !== 'string') return null;
    const text = (v: unknown): string | null => (typeof v === 'string' ? v : null);
    return { v: 1, p: c.p, us: text(c.us), um: text(c.um), uc: text(c.uc), r: text(c.r), t: c.t };
  } catch {
    return null;
  }
}
